import { useAsync } from '@nxsys/hooks/useAsync';
import {
  getLatestHistoryLeaverApi,
  reinstatementEmployeeApi,
  submitIssueP45Api,
  submitIssueP45BySubcontractorIdApi,
  submitIssueP45BySubcontractorIdIrelandApi,
  validateIssueP45Api,
  validateIssueP45BySubcontractorIdApi,
} from '@nxsys/services/api/subcontractor';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import {
  LatestHistoryLeaverData,
  ReinstatementParams,
  ReinstatementResponse,
  SubmitIssueP45BySubcontractorIdParams,
  SubmitIssueP45BySubcontractorIdResponse,
  SubmitIssueP45Params,
  SubmitIssueP45Response,
  ValidateIssueP45BySubcontractorIdResponse,
  ValidateIssueP45Params,
  ValidateIssueP45Response,
} from '@nxsys/types/api/subcontractor';
import { showMessage } from '@nxsys/utils/message';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getErrorMessage } from '@nxsys/utils/getErrorMessage';

export const useValidateIssueP45 = () => {
  const { value, loading, error, run } = useAsync<
    ValidateIssueP45Response,
    ValidateIssueP45Params
  >(validateIssueP45Api);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useSubmitIssueP45 = () => {
  const { value, loading, error, run } = useAsync<
    SubmitIssueP45Response,
    SubmitIssueP45Params
  >(submitIssueP45Api);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetLatestHistoryLeaver = () => {
  const { value, loading, error, run } = useAsync<
    { data: LatestHistoryLeaverData },
    string
  >(getLatestHistoryLeaverApi);

  return {
    value,
    loading,
    error,
    run,
  };
};

type ReinstatementApiArgs = { id: string } & ReinstatementParams;

export const useReinstatementEmployee = () => {
  const { value, loading, error, run } = useAsync<
    ReinstatementResponse,
    ReinstatementApiArgs
  >(({ id, ...params }) => reinstatementEmployeeApi(id, params));

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useValidateIssueP45BySubcontractorId = () => {
  const { value, loading, error, run } = useAsync<
    ValidateIssueP45BySubcontractorIdResponse,
    string
  >(validateIssueP45BySubcontractorIdApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useSubmitIssueP45BySubcontractorId = () => {
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;

  const { value, loading, error, run } = useAsync<
    SubmitIssueP45BySubcontractorIdResponse,
    { subcontractorId: string } & SubmitIssueP45BySubcontractorIdParams
  >(({ subcontractorId, ...params }) => isIreland ? submitIssueP45BySubcontractorIdIrelandApi(subcontractorId, params) : submitIssueP45BySubcontractorIdApi(subcontractorId, params));

  const _run = (subcontractorId: string, params: SubmitIssueP45BySubcontractorIdParams) => {
    return run({ subcontractorId, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};
